import React, { useState } from "react";
import axios from "axios";
import { Mail, ShieldCheck, Send, AlertCircle, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Request-edit-link card: gebruiker heeft een bedrijf gekozen, we sturen een
 * beveiligde magic-link naar het e-mailadres op file. Toont neutrale feedback
 * en herkent rate-limit (429).
 */
export const RequestEditLinkCard = ({ listing }) => {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [message, setMessage] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [contactBiz, setContactBiz] = useState(false);

  const send = async () => {
    if (!listing?.slug) return;
    setStatus("sending");
    setMessage("");
    try {
      const res = await axios.post(`${API}/submissions/request-edit-link`, {
        slug: listing.slug,
      });
      setStatus("sent");
      setMessage(res.data?.message || "");
      setMaskedEmail(res.data?.masked_email || "");
      setContactBiz(!!res.data?.contact_biz);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 429) {
        setStatus("rate-limit");
        setMessage(
          err.response?.data?.detail ||
            "Te veel aanvragen achter elkaar. Probeer het over een paar minuten opnieuw."
        );
      } else {
        setStatus("error");
        setMessage(
          err?.response?.data?.detail ||
            "Er ging iets mis. Probeer het zo nog eens."
        );
      }
    }
  };

  // Success-state
  if (status === "sent") {
    return (
      <div
        data-testid="edit-link-sent-card"
        className="rounded-2xl border border-[#6BC0E8] bg-[#EAF6FC] p-6 sm:p-8"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
            <ShieldCheck className="w-6 h-6 text-[#2E9CD6]" />
          </div>
          <div className="flex-1">
            <h3 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-1">
              Check je mailbox
            </h3>
            <p className="text-[#3E5871] font-[Inter] text-sm leading-relaxed">
              {message}
            </p>
            {maskedEmail && (
              <p className="mt-3 text-[#173A5E] font-[Poppins] font-semibold text-sm">
                Verstuurd naar: <span className="font-normal font-mono tracking-tight">{maskedEmail}</span>
              </p>
            )}
            {contactBiz && (
              <p className="mt-3 text-[#3E5871] text-sm">
                Geen mail bekend? Mail dan naar{" "}
                <a href="mailto:info@lemmer-leeft.nl" className="ll-link">info@lemmer-leeft.nl</a>{" "}
                — Herman helpt je verder.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isError = status === "error" || status === "rate-limit";
  const bg = isError ? "bg-[#FFF6F7] border-[#F4B5BA]" : "bg-[#F4F8FB] border-[#6BC0E8]";
  const iconBg = isError ? "text-[#CE1B2A]" : "text-[#2E9CD6]";
  const Icon = isError ? AlertCircle : Mail;

  return (
    <div
      data-testid="request-edit-link-card"
      className={`rounded-2xl border ${bg} p-6 sm:p-8`}
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
          <Icon className={`w-6 h-6 ${iconBg}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-1">
            Beveiligde toegang tot <span className="text-[#2E9CD6]">{listing?.name}</span>
          </h3>
          <p className="text-[#3E5871] font-[Inter] text-sm leading-relaxed">
            Om deze wijziging te doen, sturen we een beveiligde link naar het e-mailadres dat bij dit bedrijf bekend is.
            Pas nadat je op de link in je mail klikt, kun je de gegevens zien en bewerken.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={send}
              disabled={status === "sending"}
              data-testid="request-edit-link-btn"
              className="ll-btn-primary disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {status === "sending" ? "Bezig..." : "Stuur mij de link"}
            </button>
            <span className="text-xs text-[#5A7594] font-[Inter] inline-flex items-center gap-1">
              <Info className="w-3 h-3" /> Link geldig 45 min, éénmalig te gebruiken
            </span>
          </div>
          {isError && (
            <p className="mt-3 text-sm text-[#CE1B2A] font-[Poppins] font-semibold">
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestEditLinkCard;
